import { useEffect, useState, type DependencyList } from 'react';

/**
 * 可取消的异步加载：deps 变化时清空旧数据并重新请求，卸载/切换时 abort。
 * fetcher 为 null 表示不满足请求条件（只清空，不请求）。失败统一打日志并置 error。
 */
export function useAbortableFetch<T>(
  fetcher: ((signal: AbortSignal) => Promise<T>) | null,
  deps: DependencyList,
  errorLabel: string,
) {
  const [nodes, setNodes] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setNodes(null);
    setError(false);
    setLoading(false);
    if (!fetcher) return;
    const controller = new AbortController();
    setLoading(true);
    fetcher(controller.signal)
      .then((result) => { if (!controller.signal.aborted) setNodes(result); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error(`${errorLabel}:`, err);
        setError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // fetcher 每次渲染重建，由调用方 deps 决定何时重新请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return { nodes, loading, error, onRetry: () => setAttempt((value) => value + 1) };
}
